import React, { useMemo, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bet, BetStats } from "@/api/betting";
import { CalendarRange } from "lucide-react";

interface BettingInsightsProps {
  bets: Bet[];
  statsData?: BetStats;
  timeRange?: "7d" | "30d" | "90d" | "all";
}

// Time period formatter
const formatTimePeriod = (period: string) => {
  switch (period) {
    case "7d":
      return "Last 7 Days";
    case "30d":
      return "Last 30 Days";
    case "90d":
      return "Last 90 Days";
    case "all":
      return "All Time";
    default:
      return "All Time";
  }
};

// Streak component
const Streak: React.FC<{
  bets: Bet[];
  type: "win" | "loss";
}> = ({ bets, type }) => {
  // Get current streak
  const currentStreak = useMemo(() => {
    let streak = 0;
    const targetStatus = type === "win" ? "won" : "lost";

    // Start from the most recent bet
    for (let i = 0; i < bets.length; i++) {
      if (bets[i].status === targetStatus) {
        streak++;
      } else if (bets[i].status === "pending") {
        // Skip pending bets
        continue;
      } else {
        // Break on first non-matching completed bet
        break;
      }
    }

    return streak;
  }, [bets, type]);

  // Get longest streak
  const longestStreak = useMemo(() => {
    let longest = 0;
    let current = 0;
    const targetStatus = type === "win" ? "won" : "lost";

    for (const bet of bets) {
      if (bet.status === targetStatus) {
        current++;
        longest = Math.max(longest, current);
      } else if (bet.status !== "pending") {
        current = 0;
      }
    }

    return longest;
  }, [bets, type]);

  return (
    <div className="flex flex-col">
      <div className="text-sm text-zinc-400 mb-1">
        {type === "win" ? "Win Streak" : "Loss Streak"}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-bold">{currentStreak}</span>
        <span className="text-xs text-zinc-500">Best: {longestStreak}</span>
      </div>
    </div>
  );
};

// Sports Performance component
const SportsPerformance: React.FC<{
  bets: Bet[];
  limit?: number;
}> = ({ bets, limit = 5 }) => {
  // Calculate win rate by sport
  const sportStats = useMemo(() => {
    const stats = new Map();

    bets.forEach((bet) => {
      const sport = bet.sport || "Unknown";

      if (!stats.has(sport)) {
        stats.set(sport, { wins: 0, losses: 0, total: 0 });
      }

      const sportData = stats.get(sport);
      sportData.total++;

      if (bet.status === "won") sportData.wins++;
      else if (bet.status === "lost") sportData.losses++;
    });

    // Convert to array and calculate win rates
    return Array.from(stats.entries())
      .map(([sport, data]) => {
        const settled = data.wins + data.losses;
        return {
          sport,
          winRate: settled > 0 ? (data.wins / settled) * 100 : 0,
          total: data.total,
          settled,
          wins: data.wins,
          losses: data.losses,
        };
      })
      .filter((s) => s.settled >= 3) // Only include sports with at least 3 settled bets
      .sort((a, b) => b.winRate - a.winRate)
      .slice(0, limit);
  }, [bets, limit]);

  return (
    <div className="space-y-3">
      {sportStats.length > 0 ? (
        sportStats.map((sport) => (
          <div key={sport.sport}>
            <div className="flex justify-between items-baseline mb-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{sport.sport}</span>
                <span className="text-xs text-zinc-500">
                  ({sport.wins}-{sport.losses})
                </span>
              </div>
              <div
                className={`font-medium text-sm ${
                  sport.winRate > 55
                    ? "text-green-500"
                    : sport.winRate < 45
                      ? "text-red-500"
                      : ""
                }`}
              >
                {sport.winRate.toFixed(1)}%
              </div>
            </div>
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className={`h-full ${
                  sport.winRate > 55
                    ? "bg-green-600"
                    : sport.winRate < 45
                      ? "bg-red-600"
                      : "bg-blue-600"
                }`}
                style={{ width: `${sport.winRate}%` }}
              />
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-6 text-zinc-500 text-sm">
          Not enough settled bets to show sports performance
        </div>
      )}
    </div>
  );
};

// Recent Results Timeline
const RecentResults: React.FC<{
  bets: Bet[];
  limit?: number;
}> = ({ bets, limit = 10 }) => {
  // Get settled bets for the timeline
  const settledBets = useMemo(() => {
    return bets
      .filter((bet) => bet.status === "won" || bet.status === "lost")
      .slice(0, limit);
  }, [bets, limit]);

  return (
    <div>
      {settledBets.length > 0 ? (
        <div className="flex items-center gap-1 overflow-x-auto py-2 px-1">
          {settledBets.map((bet) => (
            <div
              key={bet.id}
              className={`min-w-8 h-8 rounded-md flex items-center justify-center text-white font-medium ${
                bet.status === "won" ? "bg-green-600" : "bg-red-600"
              }`}
              title={`${bet.sport}: ${bet.prediction}`}
            >
              {bet.status === "won" ? "W" : "L"}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-zinc-500 text-sm">
          No settled bets to display
        </div>
      )}
    </div>
  );
};

// Main component
const BettingInsights: React.FC<BettingInsightsProps> = ({
  bets,
  timeRange = "30d",
}) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>(timeRange);
  const [activeTab, setActiveTab] = useState<string>("overview");

  // Filter bets based on selected time range
  const filteredBets = useMemo(() => {
    if (selectedTimeRange === "all") return bets;

    const now = new Date();
    let cutoffDate: Date;

    switch (selectedTimeRange) {
      case "7d":
        cutoffDate = new Date(now.setDate(now.getDate() - 7));
        break;
      case "90d":
        cutoffDate = new Date(now.setDate(now.getDate() - 90));
        break;
      case "30d":
      default:
        cutoffDate = new Date(now.setDate(now.getDate() - 30));
    }

    return bets.filter((bet) => {
      const betDate = new Date(bet.created_at);
      return betDate >= cutoffDate;
    });
  }, [bets, selectedTimeRange]);

  // Calculate basic stats
  const stats = useMemo(() => {
    const totalBets = filteredBets.length;
    const pendingBets = filteredBets.filter(
      (bet) => bet.status === "pending",
    ).length;
    const wonBets = filteredBets.filter((bet) => bet.status === "won").length;
    const lostBets = filteredBets.filter((bet) => bet.status === "lost").length;
    const settledBets = wonBets + lostBets;
    const winPercentage = settledBets > 0 ? (wonBets / settledBets) * 100 : 0;

    // Sort bets by date, newest first
    const sortedBets = [...filteredBets].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    return {
      totalBets,
      pendingBets,
      wonBets,
      lostBets,
      settledBets,
      winPercentage,
      sortedBets,
    };
  }, [filteredBets]);

  return (
    <Card className="bg-black/60 backdrop-blur-sm">
      <CardHeader className="p-3 pb-0">
        <div className="flex justify-between items-center">
          <CardTitle className="text-base font-medium">
            Betting Insights
          </CardTitle>
          <Select
            value={selectedTimeRange}
            onValueChange={setSelectedTimeRange}
          >
            <SelectTrigger className="h-7 w-auto text-xs bg-zinc-800/50 border-zinc-700">
              <CalendarRange className="h-3 w-3 mr-1" />
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent className="p-3">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 bg-zinc-800/30 mb-3">
            <TabsTrigger value="overview" className="text-xs">
              Overview
            </TabsTrigger>
            <TabsTrigger value="sports" className="text-xs">
              By Sport
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0">
            {/* Summary metrics */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-zinc-800/30 rounded-lg p-3">
                <div className="text-sm text-zinc-400 mb-1">Win Rate</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold">
                    {stats.winPercentage.toFixed(1)}%
                  </span>
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {stats.wonBets} wins, {stats.lostBets} losses
                </div>
              </div>

              <div className="bg-zinc-800/30 rounded-lg p-3">
                <div className="text-sm text-zinc-400 mb-1">Bets Placed</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold">{stats.totalBets}</span>
                  {stats.pendingBets > 0 && (
                    <Badge className="bg-yellow-600/30 text-yellow-400 border-yellow-800 text-[10px]">
                      {stats.pendingBets} pending
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {formatTimePeriod(selectedTimeRange)}
                </div>
              </div>
            </div>

            {/* Streaks */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-zinc-800/30 rounded-lg p-3">
                <Streak bets={stats.sortedBets} type="win" />
              </div>

              <div className="bg-zinc-800/30 rounded-lg p-3">
                <Streak bets={stats.sortedBets} type="loss" />
              </div>
            </div>

            {/* Recent Results */}
            <div className="bg-zinc-800/30 rounded-lg p-3">
              <div className="text-sm font-medium mb-2">Recent Results</div>
              <RecentResults bets={stats.sortedBets} limit={10} />
            </div>
          </TabsContent>

          <TabsContent value="sports" className="mt-0">
            <div className="bg-zinc-800/30 rounded-lg p-3">
              <div className="text-sm font-medium mb-3">Win Rate by Sport</div>
              <SportsPerformance bets={filteredBets} limit={8} />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default BettingInsights;
